import { Fragment, ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Grid } from '@breathelife/mui';
import { AutocompleteOption, Box, SimpleCheckbox } from '@breathelife/ui-components';
import {
  AddressAutocompleteFieldBlueprint,
  FieldBlueprint,
  FieldGroupBlueprint,
  FieldTypes,
  FieldValidation,
  InformationFieldBlueprintVariant,
  isTextFieldBlueprint,
  NumberFieldValidation,
  NumberFieldValidations,
  ParticipantRole,
  ParticipantRoles,
  Validations,
} from '@zinnia/form-engine-sdk';

import { SubmitButton } from '../../../../../../Components/Button/SubmitButton';
import { getOptionsFromEnum } from '../../../../../../Helpers/options';
import { NodeDetail } from '../../../../../../Helpers/questionnaireEditor/questionnaireNodeIds';
import {
  getFieldTypeOptions,
  getInfoFieldStylingOptions,
} from '../../../../../../Helpers/questionnaireEditor/selectOptions';
import { useCarrierContext } from '../../../../../../Hooks';
import { ModalLayout } from '../../../../../../Layouts/Modal/ModalLayout';
import { QuestionnaireEditorContext } from '../../../ContextProvider/QuestionnaireEditorContextProvider';
import { QuestionnaireVersionDataContext } from '../../../ContextProvider/QuestionnaireVersionDataContextProvider';
import { EditorDropdown } from '../../Components/EditorDropdown';
import { IconNameSelector } from '../../Components/IconNameSelector';
import NavigatesToNextStepCheckbox from '../../Components/NavigatesToNextStepCheckbox';
import { AddressAutocompleteFieldEditor } from '../../Editors/AddressAutocompleteFieldEditor';
import { AgreeFieldEditor } from '../../Editors/AgreeFieldEditor';
import { CustomFieldEditor } from '../../Editors/CustomFieldEditor';
import { BooleanFieldDefaultValueEditor } from '../../Editors/defaultValueEditors/BooleanFieldDefaultValueEditor';
import { NumberFieldDefaultValueEditor } from '../../Editors/defaultValueEditors/NumberFieldDefaultValueEditor';
import { FieldDataValidateAs } from '../../Helpers/helperTypes';
import { useAddressAutocompleteFieldData } from '../useDataByField/useAddressAutocompleteFieldData';
import { useAgreeFieldData } from '../useDataByField/useAgreeFieldData';
import { useBaseFieldData } from '../useDataByField/useBaseFieldData';
import { useButtonFieldData } from '../useDataByField/useButtonFieldData';
import { useCustomFieldData } from '../useDataByField/useCustomFieldData';
import { useDefaultValue } from '../useDataByField/useDefaultValue';
import { useDropdownFieldData } from '../useDataByField/useDropdownFieldData';
import { useGetValidationOptions } from '../useDataByField/useGetValidationOptions';
import { useInformationFieldData } from '../useDataByField/useInformationFieldData';
import { useNumberFieldData } from '../useDataByField/useNumberFieldData';
import { useSignatureFieldData } from '../useDataByField/useSignatureFieldData';
import { AdvancedBlueprintOptionsForField } from './FieldEditors/AdvancedBlueprintOptionsForField';
import { AnswerNodeIdInput } from './FieldEditors/AnswerNodeIdInput';
import { FieldLayoutInputs } from './FieldEditors/FieldLayoutInputs';
import {
  ButtonTextInAllLanguagesInputs,
  PlaceholderInAllLanguagesInputs,
  TextInAllLanguagesInputs,
  TitleInAllLanguagesInputs,
} from './FieldEditors/TextInLanguagesInputs';
import { ValidateAsInput } from './FieldEditors/ValidateAsInput';

export function FieldCreator(props: {
  onCloseModal: () => void;
  onSubmit: (blueprint: FieldBlueprint) => void;
  dataLabelOptions: AutocompleteOption[];
  repeatableNodeId?: string;
  displayAsCard?: boolean;
  initialData?: Partial<FieldBlueprint>;
  fieldGroupBlueprint: FieldGroupBlueprint;
}): ReactElement | null {
  const {
    onCloseModal,
    onSubmit,
    dataLabelOptions,
    fieldGroupBlueprint,
    repeatableNodeId,
    initialData,
    displayAsCard,
  } = props;

  const { questionnaireVersionData } = useContext(QuestionnaireVersionDataContext);
  const questionnaireNodeIds = questionnaireVersionData?.questionnaireNodeIds;
  const { selectedLanguage } = useContext(QuestionnaireEditorContext);
  const { t } = useTranslation();
  const [fieldType, setFieldType] = useState<FieldTypes>(initialData?.fieldType || FieldTypes.input);
  const validationOptions = useGetValidationOptions(fieldType);
  const [validateAs, setValidateAs] = useState<FieldDataValidateAs<FieldBlueprint>['validateAs'] | undefined>(
    undefined,
  );

  useEffect(() => {
    if (validationOptions.length) {
      setValidateAs(validationOptions[0].value as FieldValidation);
    }
  }, [validationOptions, setValidateAs]);

  const { defaultValue, setDefaultValue, defaultValueValidationError } = useDefaultValue({
    validateAs: validateAs as unknown as Validations,
  });

  const [isValidBlueprintAutocompleteFields, setIsValidBlueprintAutocompleteFields] = useState<boolean>(false);

  const baseFieldData = useBaseFieldData({ initialData, dataLabelOptions });

  const agreeFieldData = useAgreeFieldData({
    initialData,
  });
  const addressAutocompleteFieldData = useAddressAutocompleteFieldData({
    initialData,
  });
  const buttonFieldData = useButtonFieldData({
    initialData,
  });

  const dropdownFieldData = useDropdownFieldData({ initialData });
  const informationFieldData = useInformationFieldData({ initialData });
  const numberFieldData = useNumberFieldData({ initialData });
  const signatureFieldData = useSignatureFieldData({ initialData });
  const customFieldData = useCustomFieldData({ initialData });

  const nodeIds: NodeDetail[] = repeatableNodeId
    ? [
        ...(questionnaireNodeIds?.notInQuestionnaire.withRepeatableAncestor[repeatableNodeId] ?? []),
        ...(questionnaireNodeIds?.inQuestionnaire.withRepeatableAncestor[repeatableNodeId] ?? []),
      ]
    : [
        ...(questionnaireNodeIds?.notInQuestionnaire.noRepeatability ?? []),
        ...(questionnaireNodeIds?.inQuestionnaire.noRepeatability ?? []),
      ];

  const fieldTypeOptions = useMemo(() => getFieldTypeOptions(t), [t]);

  const isValidBlueprint = !!fieldType && !!baseFieldData.data.answerNodeId && !!validateAs;

  const handleSubmit = useCallback(() => {
    const blueprint = {
      fieldType,
      ...baseFieldData.data,
      ...(fieldType === FieldTypes.agree && agreeFieldData.data),
      ...(fieldType === FieldTypes.autocomplete && addressAutocompleteFieldData.data),
      ...(fieldType === FieldTypes.button && buttonFieldData.data),
      ...(fieldType === FieldTypes.dropdown && dropdownFieldData.data),
      ...(fieldType === FieldTypes.information && informationFieldData.data),
      ...(fieldType === FieldTypes.number && numberFieldData.data),
      ...(fieldType === FieldTypes.signature && signatureFieldData.data),
      ...(fieldType === FieldTypes.custom && customFieldData.data),
      validateAs,
      ...([FieldTypes.radio, FieldTypes.dropdown, FieldTypes.checkboxGroup].includes(fieldType) && {
        selectOptions: [],
      }),
    } as FieldBlueprint;
    onSubmit(blueprint);
    onCloseModal();
  }, [
    onSubmit,
    onCloseModal,
    baseFieldData.data,
    agreeFieldData.data,
    addressAutocompleteFieldData.data,
    buttonFieldData.data,
    dropdownFieldData.data,
    informationFieldData.data,
    numberFieldData.data,
    signatureFieldData.data,
    customFieldData.data,
    fieldType,
    validateAs,
  ]);

  const infoFieldStylingOptions = useMemo(() => getInfoFieldStylingOptions(t), [t]);
  const participantRoleOptions = getOptionsFromEnum<ParticipantRole>(ParticipantRoles, 'participantRole');
  const { languageSettings } = useCarrierContext();
  const enabledLanguages = languageSettings.enabledLanguages;

  useEffect(() => {
    baseFieldData.clearData();
    agreeFieldData.clearData();
    addressAutocompleteFieldData.clearData();
    buttonFieldData.clearData();
    dropdownFieldData.clearData();
    informationFieldData.clearData();
    numberFieldData.clearData();
    signatureFieldData.clearData();
    customFieldData.clearData();
    setDefaultValue(undefined);
  }, [fieldType]);

  if (!selectedLanguage) {
    return null;
  }

  return (
    <Fragment>
      <ModalLayout
        maxWidth='md'
        isOpen={true}
        closeModal={onCloseModal}
        title={t('admin.questionnaireManagement.input.createField.title')}
        submitButton={
          <SubmitButton
            disabled={
              !isValidBlueprint || (fieldType === FieldTypes.autocomplete && !isValidBlueprintAutocompleteFields)
            }
            onClick={handleSubmit}
            data-testid='questionnaire-editor-field-create'
          >
            {t('cta.save')}
          </SubmitButton>
        }
      >
        <Grid container spacing={2} alignItems='center'>
          <Grid item xs={6} key={`createField-fieldType`}>
            <EditorDropdown
              id='addFieldFieldTypeDropdown'
              label={t('admin.questionnaireManagement.input.fieldType')}
              selectedOptionValue={fieldType}
              onChange={(value) => {
                if (value !== null) {
                  setFieldType(value as FieldTypes);
                }
              }}
              options={fieldTypeOptions}
            />
          </Grid>
          {fieldType === FieldTypes.number && validationOptions && (
            <Grid item xs={6} key={`createField-numericalDataType`}>
              <ValidateAsInput
                value={validateAs}
                handleChange={(value: FieldValidation) => {
                  setValidateAs(value as NumberFieldValidation);
                  if (value === NumberFieldValidations.decimal || value === NumberFieldValidations.percentage) {
                    numberFieldData.setters.setNumericalDataType('float');
                  }
                  if (value === NumberFieldValidations.integer) {
                    numberFieldData.setters.setNumericalDataType('integer');
                  }
                }}
                validationOptions={validationOptions}
              />
            </Grid>
          )}
          {fieldType === FieldTypes.button &&
            buttonFieldData.data.buttonText &&
            buttonFieldData.setters.setButtonText && (
              <Fragment>
                <ButtonTextInAllLanguagesInputs
                  value={buttonFieldData.data.buttonText}
                  handleChange={buttonFieldData.setters.setButtonText}
                />
                <Grid item xs={6} key={`createField-buttonIconName`}>
                  <IconNameSelector
                    label={t('admin.questionnaireManagement.input.iconName')}
                    value={baseFieldData.data.iconName}
                    onChange={(value) => baseFieldData.setters.setIconName(value)}
                    disabled={false}
                  />
                </Grid>
              </Fragment>
            )}
          {validationOptions && fieldType !== FieldTypes.number && (
            <Grid item xs={6} key={`createField-validationType`}>
              <ValidateAsInput
                value={validateAs}
                handleChange={(value) => (setValidateAs as (value: FieldValidation) => void)(value as FieldValidation)}
                validationOptions={validationOptions}
              />
            </Grid>
          )}

          <Grid item xs={fieldType === FieldTypes.button ? 6 : 12} key={`createField-isRequiredField`}>
            <SimpleCheckbox
              id='createField-isRequiredField'
              label={t('admin.questionnaireManagement.input.requiredField')}
              checked={!baseFieldData.data.optional}
              onChange={(event) => {
                baseFieldData.setters.setRequired(event.target.checked);
              }}
            />
          </Grid>
          <Grid item xs={12} key={`createField-isDisabledField`}>
            <SimpleCheckbox
              id='createField-isDisabledField'
              label={t('admin.questionnaireManagement.input.disabledField')}
              checked={!!baseFieldData.data.disabled}
              onChange={(event) => {
                baseFieldData.setters.setDisabled(event.target.checked);
              }}
            />
          </Grid>
          {fieldType === FieldTypes.button && (
            <Grid item xs={6} key={`createField-buttonText-triggerStepNavigation`}>
              <NavigatesToNextStepCheckbox
                prefix='create-field'
                checked={!!baseFieldData.data.triggerStepNavigation}
                onChange={baseFieldData.setters.setTriggerStepNavigation}
              />
            </Grid>
          )}

          <Grid item xs={6} key={`createField-answerNodeId`}>
            <AnswerNodeIdInput
              selectedNodeId={baseFieldData.data.answerNodeId}
              nodeIds={nodeIds}
              handleChange={(e) => {
                baseFieldData.setters.setAnswerNodeId(e || '');
              }}
            />
          </Grid>

          {fieldType === FieldTypes.signature ? (
            <Grid item xs={6} key={`createField-participantRole`}>
              <EditorDropdown
                label={t('admin.questionnaireManagement.input.participantRole')}
                selectedOptionValue={signatureFieldData.data.participantRole}
                onChange={(newValue) => {
                  signatureFieldData.setters.setParticipantRole(newValue as ParticipantRole);
                }}
                options={participantRoleOptions}
              />
            </Grid>
          ) : (
            <Grid item xs={6} key={`createField-spacing`} />
          )}

          <TitleInAllLanguagesInputs handleChange={baseFieldData.setters.setTitle} value={baseFieldData.data.title} />
          <TextInAllLanguagesInputs handleChange={baseFieldData.setters.setText} value={baseFieldData.data.text} />

          {isTextFieldBlueprint(fieldType) && (
            <PlaceholderInAllLanguagesInputs
              handleChange={baseFieldData.setters.setPlaceholder}
              value={baseFieldData.data.placeholder}
            />
          )}
          {fieldType === FieldTypes.information && (
            <Grid item xs={6} key={'createField-informationFieldVariant'}>
              <EditorDropdown
                id='informationFieldVariant'
                label={t('admin.questionnaireManagement.input.infoFieldStyling')}
                selectedOptionValue={informationFieldData.data?.variant}
                onChange={(newValue) => {
                  if (newValue !== null) {
                    informationFieldData.setters.setVariant(newValue as InformationFieldBlueprintVariant);
                  }
                }}
                options={infoFieldStylingOptions}
              />
            </Grid>
          )}
          {fieldType === FieldTypes.autocomplete && (
            <Box pb={1} pl={2} pt={1}>
              <AddressAutocompleteFieldEditor
                fieldGroupBlueprint={fieldGroupBlueprint}
                blueprint={
                  { ...baseFieldData.data, ...addressAutocompleteFieldData.data } as AddressAutocompleteFieldBlueprint
                }
                nodeIds={nodeIds}
                disabled={false}
                onIsValidChange={setIsValidBlueprintAutocompleteFields}
                setters={{
                  setPostalCodeOrZip: addressAutocompleteFieldData.setters.setPostalCodeOrZip,
                  setStateOrProvince: addressAutocompleteFieldData.setters.setStateOrProvince,
                  setCountryCode: addressAutocompleteFieldData.setters.setCountryCode,
                  setCity: addressAutocompleteFieldData.setters.setCity,
                  setAddressAutocompleteNodeId: addressAutocompleteFieldData.setters.setAddressAutocompleteNodeId,
                  setStreetAddress: addressAutocompleteFieldData.setters.setStreetAddress,
                  setApartment: addressAutocompleteFieldData.setters.setApartment,
                }}
              />
            </Box>
          )}
          {(fieldType === FieldTypes.number || fieldType === FieldTypes.money) && (
            <Grid item xs={6} key={'createField-defaultNumberValueEditor'}>
              <NumberFieldDefaultValueEditor
                fieldType={fieldType}
                value={defaultValue as number}
                onChange={(value?: number) => {
                  setDefaultValue(value);
                }}
                validationErrorFromProps={defaultValueValidationError}
                numericalDataType={fieldType === FieldTypes.number ? numberFieldData.data.numericalDataType : undefined}
                disabled={false}
              />
            </Grid>
          )}

          {fieldType === FieldTypes.checkbox && (
            <Grid item xs={6} key={'createField-defaultCheckboxValueEditor'}>
              <BooleanFieldDefaultValueEditor
                onChange={(value) => {
                  setDefaultValue(value);
                }}
                value={false}
                disabled={false}
              />
            </Grid>
          )}

          {fieldType === FieldTypes.agree && (
            <Box pb={1} pl={2} pt={1}>
              <AgreeFieldEditor
                languages={enabledLanguages}
                disabled={false}
                consentModalHeader={agreeFieldData.data.modalHeader || {}}
                consentText={agreeFieldData.data.modalText || {}}
                onConsentModalHeaderChange={(value, language) => agreeFieldData.setters.setModalHeader(language, value)}
                onConsentTextChange={(value, language) => agreeFieldData.setters.setModalText(language, value)}
                spacing={2}
              />
            </Box>
          )}

          {fieldType === FieldTypes.custom && (
            <Box pb={1} pl={2} pt={1}>
              <CustomFieldEditor
                onCustomNameChange={(value) => customFieldData.setters.setCustomName(value)}
                onCustomNameBlur={() => {}}
              />
            </Box>
          )}

          <FieldLayoutInputs
            sizeValue={baseFieldData.data.layout?.size}
            handleSizeChange={baseFieldData.setters.setSize as any}
            forceNewLineValue={baseFieldData.data.layout?.forceNewLine}
            handleForceNewLineChange={baseFieldData.setters.setForceNewLine}
          />

          {fieldType === FieldTypes.dropdown && (
            <Grid item xs={12} key={`createField-isSearchableField`}>
              <SimpleCheckbox
                id='createField-isSearchableField'
                label={t('admin.questionnaireManagement.input.searchable')}
                checked={!!dropdownFieldData.data.searchable}
                onChange={(event) => {
                  dropdownFieldData.setters.setSearchable(event.target.checked);
                }}
              />
            </Grid>
          )}

          {displayAsCard && (
            <Box pt={2}>
              <SimpleCheckbox
                id='createField-displayInCardPreview'
                label={t('admin.questionnaireManagement.input.displayInCardPreview')}
                checked={!!baseFieldData.data.displayInCardPreview}
                onChange={(event) => {
                  baseFieldData.setters.setDisplayInCardPreview(event.target.checked);
                }}
              />
            </Box>
          )}

          <Box pt={2}>
            <AdvancedBlueprintOptionsForField
              data={baseFieldData.data}
              setters={baseFieldData.setters}
              dataLabelOptions={dataLabelOptions}
            />
          </Box>
        </Grid>
      </ModalLayout>
    </Fragment>
  );
}
